import { TEXT_TRUNCATION } from "./constants";
import type { DailyCheckIn, ChartData } from "./types";

/**
 * Extracts first N sentences from text
 */
export function getFirstSentences(text: string, count: number): string {
	if (!text) return "";

	const sentences = text.match(/[^.!?]+[.!?]+/g) || [];

	if (sentences.length > 0) {
		return sentences.slice(0, count).join(" ").trim();
	}

	const fallbackSentences = text
		.split(/[.!?]+/)
		.filter((s) => s.trim().length > 0)
		.slice(0, count);

	if (fallbackSentences.length > 0) {
		return fallbackSentences.join(". ").trim();
	}

	const maxLength =
		count === TEXT_TRUNCATION.SENTENCES_TWO
			? TEXT_TRUNCATION.SHORT_DESCRIPTION
			: TEXT_TRUNCATION.MEDIUM_DESCRIPTION;

	return text.length > maxLength
		? `${text.substring(0, maxLength)}...`
		: text.trim();
}

/**
 * Calculates consecutive day streak from check-ins
 */
export function calculateDayStreak(checkIns: DailyCheckIn[]): number {
	if (checkIns.length === 0) return 0;

	const sortedCheckIns = [...checkIns].sort(
		(a, b) =>
			new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
	);

	let streak = 0;
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const latestCheckIn = new Date(sortedCheckIns[0].created_at);
	latestCheckIn.setHours(0, 0, 0, 0);

	const daysSinceLatest = Math.floor(
		(today.getTime() - latestCheckIn.getTime()) / (1000 * 60 * 60 * 24),
	);

	if (daysSinceLatest > 1) return 0;

	const checkInDates = new Set(
		sortedCheckIns.map((checkIn) => {
			const date = new Date(checkIn.created_at);
			date.setHours(0, 0, 0, 0);
			return date.getTime();
		}),
	);

	let currentDate = new Date(latestCheckIn);
	while (checkInDates.has(currentDate.getTime())) {
		streak++;
		currentDate.setDate(currentDate.getDate() - 1);
	}

	return streak;
}

/**
 * Prepares chart data with rolling average
 */
export function prepareChartData(
	checkIns: DailyCheckIn[],
	windowSize: number = 7,
): ChartData[] {
	const chartPoints: ChartData[] = [];
	let cumulativeScore = 0;

	checkIns.forEach((checkIn) => {
		cumulativeScore += checkIn.general_feeling;
		chartPoints.push({
			date: new Date(checkIn.created_at).toLocaleDateString("en-US", {
				month: "short",
				day: "numeric",
			}),
			feeling: cumulativeScore,
			label: new Date(checkIn.created_at).toLocaleDateString(),
			checkInId: checkIn.id,
			generalFeeling: checkIn.general_feeling,
			description: checkIn.day_description || "",
			reflectionId: checkIn.reflection_id,
		});
	});

	return chartPoints.map((point, index) => {
		const window = Math.min(windowSize, index + 1);
		const windowStart = Math.max(0, index - window + 1);
		const windowData = chartPoints.slice(windowStart, index + 1);
		const average =
			windowData.reduce((sum, p) => sum + p.feeling, 0) / windowData.length;

		return {
			...point,
			rollingAverage: average,
		};
	});
}

/**
 * Extracts top N topic tags from reflections
 */
export function getTopTopicTags(
	reflections: Array<{ topic_tags?: string[] }>,
	limit: number = 5,
): string[] {
	const allTags = reflections.flatMap((r) => r.topic_tags || []);
	const tagCounts = allTags.reduce(
		(acc, tag) => {
			acc[tag] = (acc[tag] || 0) + 1;
			return acc;
		},
		{} as Record<string, number>,
	);

	return Object.entries(tagCounts)
		.sort(([, a], [, b]) => b - a)
		.slice(0, limit)
		.map(([tag]) => tag);
}
